import React, { useEffect, useState } from "react";
import API_BASE from "../config/api";
import ImageUploadField from "./ImageUploadField";
import { getAdminToken, adminFetch } from "../utils/adminAuth";

/*
  CR "Homepage / Landing Page — Dynamic landing page images" (tracker item W3).
  Manages the 3 big carousel slides + 4 right-side promo cards shown by
  AdsSection.js on the public homepage, replacing what used to be hard-coded
  /assets/*.png paths in that component.
*/
const BLANK_BIG = { img: "", btn: "Call Now", link: "" };
const BLANK_RIGHT = { img: "", btn: "Call Now", link: "" };

export default function AdminHomepageImages() {
    const [bigSlides, setBigSlides] = useState([{ ...BLANK_BIG }, { ...BLANK_BIG }, { ...BLANK_BIG }]);
    const [rightCards, setRightCards] = useState([{ ...BLANK_RIGHT }, { ...BLANK_RIGHT }, { ...BLANK_RIGHT }, { ...BLANK_RIGHT }]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);

    const authHeaders = { "X-Admin-Token": getAdminToken() };

    useEffect(() => {
        fetch(`${API_BASE}/api/get_homepage_ads.php`)
            .then((r) => r.json())
            .then((data) => {
                if (data.success) {
                    if (data.bigSlides?.length) setBigSlides(data.bigSlides);
                    if (data.rightCards?.length) setRightCards(data.rightCards);
                }
            })
            .finally(() => setLoading(false));
    }, []);

    const updBig = (i, key, val) => { const c = [...bigSlides]; c[i] = { ...c[i], [key]: val }; setBigSlides(c); };
    const updRight = (i, key, val) => { const c = [...rightCards]; c[i] = { ...c[i], [key]: val }; setRightCards(c); };

    const handleSave = async () => {
        setSaving(true);
        setMsg(null);
        try {
            const res = await adminFetch(`${API_BASE}/api/save_homepage_ads.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ bigSlides, rightCards }),
            });
            const data = await res.json();
            setSaving(false);
            if (data.success) setMsg({ type: "success", text: "Homepage images saved. They'll appear on the live site immediately." });
            else setMsg({ type: "danger", text: data.message || "Could not save." });
        } catch {
            setSaving(false);
            setMsg({ type: "danger", text: "Network error while saving." });
        }
    };

    if (loading) return <div className="p-3">Loading current homepage images...</div>;

    return (
        <div>
            <h5 className="fw-bold mb-1">Homepage Images</h5>
            <p className="text-secondary small mb-3">
                These are the banner slides and promo cards on the public homepage. Each slot needs an image; the button text and
                link are optional (leave the link blank for a non-clickable button).
            </p>

            {msg && <div className={`alert alert-${msg.type} py-2`}>{msg.text}</div>}

            <div className="card shadow-sm mb-3">
                <div className="card-header bg-light fw-semibold">Main Carousel (big slides)</div>
                <div className="card-body">
                    {bigSlides.map((s, i) => (
                        <div className="border rounded p-2 mb-2" key={i}>
                            <div className="row g-2 align-items-start">
                                <div className="col-md-6">
                                    <ImageUploadField
                                        value={s.img}
                                        onChange={(v) => updBig(i, "img", v)}
                                        purpose="homepage_ad"
                                        placeholder={`Upload slide ${i + 1} image`}
                                        allowUrl={false}
                                        authHeaders={authHeaders}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small">Button Text</label>
                                    <input className="form-control form-control-sm" value={s.btn} onChange={(e) => updBig(i, "btn", e.target.value)} />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small">Link (optional)</label>
                                    <input className="form-control form-control-sm" placeholder="https://..." value={s.link} onChange={(e) => updBig(i, "link", e.target.value)} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="card shadow-sm mb-3">
                <div className="card-header bg-light fw-semibold">Side Promo Cards</div>
                <div className="card-body">
                    {rightCards.map((s, i) => (
                        <div className="border rounded p-2 mb-2" key={i}>
                            <div className="row g-2 align-items-start">
                                <div className="col-md-6">
                                    <ImageUploadField
                                        value={s.img}
                                        onChange={(v) => updRight(i, "img", v)}
                                        purpose="homepage_ad"
                                        placeholder={`Upload card ${i + 1} image`}
                                        allowUrl={false}
                                        authHeaders={authHeaders}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small">Button Text</label>
                                    <input className="form-control form-control-sm" value={s.btn} onChange={(e) => updRight(i, "btn", e.target.value)} />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small">Link (optional)</label>
                                    <input className="form-control form-control-sm" placeholder="https://..." value={s.link} onChange={(e) => updRight(i, "link", e.target.value)} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="d-flex justify-content-end">
                <button className="btn btn-primary px-4" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Homepage Images"}</button>
            </div>
        </div>
    );
}
